import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Landmark, Search, FileCode2, ExternalLink, Clock, Lock, Mail, FileDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useShopperAuth } from "@/context/ShopperAuthContext";
import { getApiUrl } from "@/utils/api";
import { getCapturedPropertyDetails, mergeCapturedPropertyDetails } from "@/utils/capturedPropertyCookie";
import { GoogleLogo } from "@/components/icons/GoogleLogo";

const API = getApiUrl();
// sessionStorage key used to resume a search after the Google SSO redirect
// round-trip (full page nav loses React state) — see `HousePricesModule`.
const PENDING_KEY = "slh_house_prices_pending_postcode";

const CONFIDENCE_STYLE = {
  high: "bg-emerald-100 text-emerald-800 border-emerald-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-rose-100 text-rose-800 border-rose-200",
};

const PLANNING_STATUS_STYLE = {
  Permitted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Conditions: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-800 border-rose-200",
  Withdrawn: "bg-rose-100 text-rose-800 border-rose-200",
  Undecided: "bg-amber-100 text-amber-800 border-amber-200",
  Referred: "bg-amber-100 text-amber-800 border-amber-200",
  Unresolved: "bg-amber-100 text-amber-800 border-amber-200",
  Unknown: "bg-muted text-muted-foreground border-border",
};

function formatGBP(n) {
  if (n === null || n === undefined) return "—";
  return `£${Number(n).toLocaleString("en-GB")}`;
}

function formatDMY(isoDate) {
  if (!isoDate) return "";
  const parts = String(isoDate).slice(0, 10).split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
}

/**
 * Agent dashboard → "House Prices" section. Standalone postcode-only price
 * estimate tool (e.g. for agents fielding "what's my house worth?" from a
 * prospective seller) — same HM Land Registry + Claude pipeline as the
 * per-listing "Estimate Price" CTA, just without a specific property.
 */
export default function HousePricesTool({
  publicMode = false, thankYouCtaText = "", thankYouCtaHref = null,
}) {
  const [postcode, setPostcode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const { shopper, signIn } = useShopperAuth();
  const [consent, setConsent] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimedEmail, setClaimedEmail] = useState(null);

  const claimResult = async (pc) => {
    setClaiming(true);
    try {
      const { data } = await axios.post(
        `${API}/public/house-prices/claim`,
        { postcode: pc, consent: true },
        { withCredentials: true }
      );
      setClaimedEmail(data.email);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || "Could not email your results — please try again.";
      toast.error(msg);
    } finally {
      setClaiming(false);
    }
  };

  // Resume an anonymous search after the Google SSO redirect round-trip —
  // the page fully reloads, so the postcode/result state was lost; we only
  // kept the postcode in sessionStorage and re-fetch via the gate's cache.
  useEffect(() => {
    if (!publicMode || !shopper) return;
    const pending = sessionStorage.getItem(PENDING_KEY);
    if (!pending) return;
    sessionStorage.removeItem(PENDING_KEY);
    setPostcode(pending);
    claimResult(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopper, publicMode]);

  // Pre-fill the postcode from the "capturedpropertydetails" cookie left
  // behind by the "List Your Property" form, if the visitor already typed
  // an address there — only when the box is still empty.
  useEffect(() => {
    if (postcode) return;
    const captured = getCapturedPropertyDetails();
    if (captured?.postcode) setPostcode(captured.postcode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror the postcode back into the same cookie as the visitor types it
  // here — so if they check House Prices first, the capture form can
  // pre-fill it for them later. Merges rather than overwrites, so any
  // street/town already saved from the capture form survives.
  useEffect(() => {
    if (!postcode) return;
    mergeCapturedPropertyDetails({ postcode });
  }, [postcode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pc = postcode.trim();
    if (!pc) return;
    setLoading(true);
    setError(null);
    setClaimedEmail(null);
    setConsent(false);
    try {
      const { data } = await axios.post(
        `${API}/${publicMode ? "public" : "property-admin"}/house-prices/estimate`,
        { postcode: pc },
        { withCredentials: !publicMode }
      );
      setResult(data);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2?.message || "Failed to estimate price";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGateUnlock = () => {
    const pc = postcode.trim();
    if (!shopper) {
      sessionStorage.setItem(PENDING_KEY, pc);
      signIn();
      return;
    }
    claimResult(pc);
  };

  const soldComps = result?.comparables || [];

  return (
    <div className="space-y-6" data-testid="house-prices-tool">
      <div className="bg-white border border-border p-6">
        <h3 className="font-serif text-xl font-medium flex items-center gap-2">
          <Landmark className="h-5 w-5 text-emerald-600" />
          House Prices
        </h3>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          {publicMode
            ? "Type any UK postcode to get an AI price estimate grounded in real HM Land Registry sold prices."
            : "Type any UK postcode to get an AI price estimate grounded in real HM Land Registry sold prices — handy when a prospective seller asks \"what's my house worth?\"."}
        </p>

        <form onSubmit={handleSubmit} className="flex items-center gap-2 max-w-md">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g. EN11 0RD"
              className="pl-9 rounded-none"
              data-testid="house-prices-postcode-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading || !postcode.trim()}
            className="rounded-none"
            data-testid="house-prices-submit-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Get Estimate"}
          </Button>
        </form>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="house-prices-loading">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground text-center px-4">
            Looking up sold prices in the area and reasoning about a fair range…
          </p>
        </div>
      )}

      {!loading && error && (
        <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md p-4" data-testid="house-prices-error">
          {error}
        </div>
      )}

      {claiming && !claimedEmail && (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground" data-testid="house-prices-claiming">
          <Loader2 className="h-4 w-4 animate-spin" /> Unlocking your results…
        </div>
      )}

      {claimedEmail && publicMode && (
        <div className="bg-white border border-emerald-200 bg-emerald-50/50 p-6 text-center space-y-2" data-testid="house-prices-thank-you">
          <Mail className="h-8 w-8 text-emerald-600 mx-auto" />
          <h4 className="font-serif text-lg font-medium">Thanks — check your email!</h4>
          <p className="text-sm text-muted-foreground">
            We've sent your full house price summary for <strong>{postcode.toUpperCase()}</strong> to <strong>{claimedEmail}</strong>.
          </p>
          {thankYouCtaText?.trim() && thankYouCtaHref && (
            <div className="pt-3">
              <Link
                to={thankYouCtaHref}
                className="inline-flex items-center justify-center gap-2 font-semibold transition-all duration-200 shadow hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap px-6 py-3 rounded-full"
                style={{ backgroundColor: "var(--button-bg, var(--primary, #2f4f4f))", color: "var(--button-text, #ffffff)" }}
                data-testid="house-prices-thank-you-cta"
              >
                {thankYouCtaText}
              </Link>
            </div>
          )}
        </div>
      )}

      {!loading && !claiming && result && publicMode && !claimedEmail && (
        <div className="bg-white border border-border p-6 space-y-4" data-testid="house-prices-gate">
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-emerald-600" />
            <h4 className="font-serif text-lg font-medium">Your results are ready</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            {shopper
              ? <>You're signed in as <strong>{shopper.email}</strong> — tick the box to email yourself the full estimate for <strong>{postcode.toUpperCase()}</strong>.</>
              : <>Sign in with Google to unlock your estimate for <strong>{postcode.toUpperCase()}</strong> — we'll email you the full summary, sold comparables and nearby planning activity.</>}
          </p>
          <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="house-prices-gdpr-consent-label">
            <Checkbox
              checked={consent}
              onCheckedChange={(v) => setConsent(!!v)}
              className="mt-0.5"
              data-testid="house-prices-gdpr-consent-checkbox"
            />
            <span className="text-muted-foreground">
              I agree to be emailed my house price results for this search, in line with the Privacy Policy. No marketing emails — just this one summary.
            </span>
          </label>
          <Button
            type="button"
            onClick={handleGateUnlock}
            disabled={claiming || !consent}
            className="rounded-none w-full sm:w-auto"
            data-testid="house-prices-gate-unlock-btn"
          >
            {claiming ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : shopper ? (
              <Mail className="h-4 w-4 mr-2" />
            ) : (
              <GoogleLogo />
            )}
            {shopper ? "Email my results" : "Sign in with Google"}
          </Button>
        </div>
      )}

      {!loading && result && !publicMode && (
        <div className="bg-white border border-border p-6 space-y-5" data-testid="house-prices-result">
          {result.cached && (
            <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="house-prices-cached-indicator">
              <Clock className="h-3 w-3" /> Cached result from {formatDMY(result.cached_at)}
            </p>
          )}
          <div className="flex items-start justify-between flex-wrap gap-3 border rounded-lg p-4 bg-muted/30">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground mb-1">Estimated range</p>
              <p className="text-2xl font-bold break-words" data-testid="house-prices-range">
                {formatGBP(result.estimated_low)} – {formatGBP(result.estimated_high)}
              </p>
            </div>
            <Badge
              className={`border shrink-0 ${CONFIDENCE_STYLE[result.confidence] || CONFIDENCE_STYLE.low}`}
              data-testid="house-prices-confidence"
            >
              {(result.confidence || "low").toUpperCase()} CONFIDENCE
            </Badge>
          </div>

          {result.document_id && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-none text-xs"
              onClick={() => { window.location.href = `${API}/agent-documents/${result.document_id}/download`; }}
              data-testid="house-prices-download-pdf-btn"
            >
              <FileDown className="mr-1.5 h-3.5 w-3.5" />
              Download PDF
            </Button>
          )}

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Reasoning</p>
            <p className="text-sm leading-relaxed" data-testid="house-prices-reasoning">{result.reasoning}</p>
          </div>

          {soldComps.length > 0 ? (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
                <Landmark className="h-3.5 w-3.5 shrink-0" />
                Sold comparables ({soldComps.length}) — HM Land Registry
              </p>
              <div className="space-y-1.5" data-testid="house-prices-sold-comps">
                {soldComps.map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-sm border rounded-md px-3 py-2">
                    <span className="truncate min-w-0 flex-1">{c.address}</span>
                    <span className="shrink-0 text-muted-foreground text-xs">{formatDMY(c.date)}</span>
                    <span className="shrink-0 font-medium">{formatGBP(c.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              No direct sold comparables were found for this postcode — this estimate relies on general UK market knowledge only.
            </p>
          )}

          {Array.isArray(result.planning_applications) && result.planning_applications.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
                <FileCode2 className="h-3.5 w-3.5 shrink-0" />
                Nearby planning applications ({result.planning_applications.length}) — PlanIt
              </p>
              <div className="space-y-1.5" data-testid="house-prices-planning-apps">
                {result.planning_applications.map((a, i) => (
                  <a
                    key={i}
                    href={a.link || undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="block border rounded-md px-3 py-2 text-sm hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="truncate min-w-0 flex-1 font-medium">{a.address}</span>
                      <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${PLANNING_STATUS_STYLE[a.status] || PLANNING_STATUS_STYLE.Unknown}`}>
                        {a.status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</p>
                    <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1">
                      {formatDMY(a.date)}
                      {a.link && <ExternalLink className="h-3 w-3 ml-1" />}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
